import { ColorType, createChart, UTCTimestamp } from "lightweight-charts";
import { useEffect, useRef } from "react";
import { DataPoint } from "./types";

const detectIntersection = (line1, line2, index) => {
  const x1 = parseFloat(line1?.[index]?.time);
  const x2 = parseFloat(line1?.[index + 1]?.time);
  const x3 = parseFloat(line2?.[index]?.time);
  const x4 = parseFloat(line2?.[index + 1]?.time);
  const y1 = parseFloat(line1?.[index]?.value);
  const y2 = parseFloat(line1?.[index + 1]?.value);
  const y3 = parseFloat(line2?.[index]?.value);
  const y4 = parseFloat(line2?.[index + 1]?.value);

  const denominator = ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1))

  // Lines are parallel
  if (denominator === 0) {
    return false
  }

  let ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator
  let ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator

  // is the intersection along the segments
  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
    return false
  }

  // Return a object with the x and y coordinates of the intersection
  let time = parseInt(x1 + ua * (x2 - x1));
  let value = y1 + ua * (y2 - y1)

  return { time, value }
}

interface Props {
  data: {
    high: DataPoint[];
    mid: DataPoint[];
    low: DataPoint[];
  };
  backgroundColor?: string;
  textColor?: string;
  showTime?: boolean;
  curvedLines?: boolean;
  ticker?: string;
}

const TradingRangeChart = ({
  data,
  backgroundColor = "transparent",
  textColor = "#999",
  showTime,
  curvedLines,
  ticker
}: Props) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const handleResize = () => {
      chart.applyOptions({ width: chartContainerRef?.current?.clientWidth });
    };

    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: backgroundColor },
        textColor,
      },
      grid: {
        vertLines: {
          color: "#222",
        },
        horzLines: {
          color: "#222",
        },
      },
      width: chartContainerRef.current.clientWidth,
      // take container height or default to 300
      height: chartContainerRef.current.clientHeight || 300,
      // show or hide time based on prop
      timeScale: showTime
        ? {
          timeVisible: true,
          secondsVisible: false,
        }
        : undefined,
      localization: {
        priceFormatter: Intl.NumberFormat("en-US", {
          style: "decimal",
        }).format,
      },
    });
    chart.timeScale().fitContent();

    const highSeries = chart.addAreaSeries({
      lineColor: "#d33541",
      topColor: "rgba(255, 0, 0, 0.4)",
      bottomColor: "transparent",
      lineType: curvedLines ? 2 : 0,
    });
    highSeries.setData(data.high);

    const midSeries = chart.addAreaSeries({
      lineColor: "#fff",
      topColor: "rgba(0, 255, 0, 0.4)",
      bottomColor: "transparent",
      lineType: curvedLines ? 2 : 0,
    });
    midSeries.setData(data.mid);

    const lowSeries = chart.addAreaSeries({
      lineColor: "#75ce41",
      topColor: "transparent",
      bottomColor: "transparent",
      lineType: curvedLines ? 2 : 0,
    });
    lowSeries.setData(data.low);

    // const intersectionData = [];
    // const intersectionMarkers = [];
    // for (let i = 0; i < data.mid.length; i++) {
    //   let intersection = detectIntersection(data.high, data.mid, i);
    //   if (!intersection) {
    //     intersection = detectIntersection(data.low, data.mid, i);
    //   }
    //   if (!isNaN(intersection?.time)) {
    //     intersectionData.push({ time: intersection.time, value: intersection.value });
    //     intersectionMarkers.push({ time: intersection.time, position: 'inBar', color: intersection.value < data.mid[i].value ? 'green' : 'red', shape: 'circle' });
    //   }
    // }
    // const markerSeries = chart.addAreaSeries({
    //   lineColor: "transparent",
    //   topColor: "transparent",
    //   bottomColor: "transparent",
    //   lineType: 0,
    // });
    // markerSeries.setData(data.mid);
    // markerSeries.setMarkers(intersectionMarkers);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      chart.remove();
    };
  }, [data, backgroundColor, textColor, showTime, curvedLines]);

  return <div style={{ height: "100%" }} ref={chartContainerRef} />;
};

export default TradingRangeChart;
